import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Grid, Stack, Typography } from '@mui/material';
import { EmojiEvents } from '@mui/icons-material';
import { achievementsV2API } from '../../services/api';
import { tokens } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';

export default function AchievementsPanel({ userId }) {
  const { previewMode } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [unlocks, setUnlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Rendered unconditionally from InsightsAnalytics (/dashboard/insights),
    // which a preview visitor can reach via the nav rail. userId is the
    // truthy 'preview-user' id, so without this guard the request 401s and
    // the auth interceptor bounces the preview session to /login. previewMode
    // comes from AuthContext (captured once at session establishment), not
    // re-derived from window.location.search, which in-app navigation
    // rewrites without the query string.
    if (!userId || previewMode) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [templatesRes, unlocksRes] = await Promise.all([
          achievementsV2API.getTemplates(),
          achievementsV2API.getUserUnlocks(userId),
        ]);
        if (!cancelled) {
          setTemplates(templatesRes.data?.data || []);
          setUnlocks(unlocksRes.data?.data || []);
        }
      } catch {
        if (!cancelled) {
          setTemplates([]);
          setUnlocks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [previewMode, userId]);

  const unlockedIds = useMemo(() => new Set(unlocks.map((u) => u.templateId)), [unlocks]);
  const totalPoints = useMemo(
    () => unlocks.reduce((sum, item) => sum + (item.points || 0), 0),
    [unlocks]
  );

  if (loading) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <EmojiEvents sx={{ color: tokens.accentStrong }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Achievements</Typography>
        <Chip size="small" label={`${unlocks.length} unlocked · ${totalPoints} pts`} />
      </Stack>
      <Grid container spacing={1.5}>
        {templates.map((template) => {
          const unlocked = unlockedIds.has(template.templateId);
          return (
            <Grid item xs={12} sm={6} md={4} key={template.templateId}>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: unlocked ? tokens.accent : tokens.border,
                  bgcolor: unlocked ? 'rgba(169, 191, 122, 0.12)' : tokens.surface,
                  opacity: unlocked ? 1 : 0.65,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {template.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {template.description}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                  <Chip size="small" label={`${template.points} pts`} />
                  <Chip size="small" variant="outlined" label={template.category} />
                </Stack>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
